<script lang="ts" setup>
//#region Import's

import { onMounted, onUnmounted, ref } from 'vue';
import { useRouter } from 'vue-router';

// Service / Store
import { useStepVistoria } from '@/stores/stepVistoria';
import {
  deleteFile,
  getFileAndObservation,
  saveObservation,
} from '@/service/vistoria-files/index';
import {
  analyzeStepPhoto,
  commitStepPhoto,
  isStepPhotoDraft,
  savePreAnalysisStep,
  withoutPreAnalysis,
} from '@/service/pre-analysis';

// Component
import HeaderProgress from '@/shared/component/header/header-progress.vue';
import IconCamera from '@/shared/icons/IconCamera.vue';
import IconCheck from '@/shared/icons/IconCheck.vue';
import IconExclamation from '@/shared/icons/IconExclamation.vue';
import IconPaperAirplane from '@/shared/icons/IconPaperAirplane.vue';
import IconVideoCamera from '@/shared/icons/IconVideoCamera.vue';
import Loading from '@/shared/component/show-loading.vue';
import ModalWaitSaveFile from '@/shared/component/modal-wait-save-file.vue';
import PreviewFile from '@/shared/component/preview-file.vue';

// Other
import {
  PRE_ANALYSIS_MODE_OFF,
  PRE_ANALYSIS_MODE_ON,
  PRE_ANALYSIS_RESULT_REJECTED,
  ROTA_VISTORIA,
  TIPO_ETAPA_FOTO,
  TIPO_ETAPA_VIDEO,
  TYPE_MINE_PDF,
} from '@/global';
import { goVistoria } from '@/shared/utils/routeNavigate';

//#endregion

const router = useRouter();
const vistoriaStore = useStepVistoria();

const loading = ref(true);
const fileContent = ref('');
const observacao = ref('');
const showObservacao = ref<boolean>(true);
const typeMine = ref<string>();

const analyzingPhoto = ref(false);
const photoApproved = ref(false);
const preAnalysisRejectionReason = ref<string | null>(null);
const canSendAnyway = ref(false);

const APPROVED_MESSAGE_DURATION_MS = 1500;

const showPreAnalysisRejected = (reason: string | null, rejections: number) => {
  preAnalysisRejectionReason.value = reason;
  canSendAnyway.value =
    rejections > vistoriaStore.preAnalysisConfig.retriesToAllowSend;
};

const showPhotoApproved = async () => {
  photoApproved.value = true;
  await new Promise((resolve) =>
    setTimeout(resolve, APPROVED_MESSAGE_DURATION_MS),
  );
  photoApproved.value = false;
};

const commitAndNext = async (stepId: string, key: string) => {
  await commitStepPhoto(stepId, key);
  await vistoriaStore.nextVistoriaEtapa();
};

let tipoArquivo: string;
let fraseLegivel: string;
let fraseRetry: string;

/**
 * Avança para a próxima etapa
 * ou para o Resumo
 */
const nextStep = async () => {
  const key = vistoriaStore.vistoria!.chave;
  const step = vistoriaStore.currentVistoriaEtapa!;

  await saveObservation(key, step.idEtapa, observacao.value);

  const fileId = step.fotoVideo?.nome?.id;
  const { mode } = vistoriaStore.preAnalysisConfig;

  if (!fileId || step.sync || !(await isStepPhotoDraft(step.idEtapa, key))) {
    await vistoriaStore.nextVistoriaEtapa();
    return;
  }

  const shouldAnalyze =
    step.tipo === TIPO_ETAPA_FOTO && mode !== PRE_ANALYSIS_MODE_OFF;

  if (shouldAnalyze) {
    analyzingPhoto.value = true;
    const { preAnalysis, rejections } = await analyzeStepPhoto(
      key,
      step.idEtapa,
      step.fotoVideo!.idModeloFoto,
      fileId,
      mode,
    );
    analyzingPhoto.value = false;

    if (
      mode === PRE_ANALYSIS_MODE_ON &&
      preAnalysis.result === PRE_ANALYSIS_RESULT_REJECTED
    ) {
      showPreAnalysisRejected(preAnalysis.rejectionReason, rejections);
      return;
    }
  } else {
    await savePreAnalysisStep(
      step.idEtapa,
      key,
      fileId,
      withoutPreAnalysis(mode),
    );
  }

  await showPhotoApproved();
  await commitAndNext(step.idEtapa, key);
};

const sendAnyway = async () => {
  preAnalysisRejectionReason.value = null;
  await commitAndNext(
    vistoriaStore.currentVistoriaEtapa!.idEtapa,
    vistoriaStore.vistoria!.chave,
  );
};

/**
 * Tirar uma nova foto/video
 */
const retryPhotoVideo = async () => {
  const chave = vistoriaStore.vistoria!.chave;
  const idEtapa = vistoriaStore.currentVistoriaEtapa!.idEtapa;

  await deleteFile(idEtapa, chave);

  // Fotos via Streaming da Câmera - Desativado em 08/05
  // if (
  //   vistoriaStore.currentVistoriaEtapa?.fotoVideo?.tipoFoto ===
  //   TIPO_FOTO_DOCUMENTACAO
  // ) {
  //   router.replace({ name: ROTA_VISTORIA });
  //   return;
  // }

  // if (vistoriaStore.currentVistoriaEtapa?.tipo === TIPO_ETAPA_FOTO) {
  //   router.replace({ name: ROTA_FOTO });
  //   return;
  // }

  // router.replace({ name: ROTA_VIDEO });

  // Video via Streaming da Câmera depente da permissão do usuário - 30/06
  // if (vistoriaStore.currentVistoriaEtapa?.tipo === TIPO_ETAPA_VIDEO) {
  //   router.replace({ name: ROTA_VIDEO });
  //   return;
  // }

  router.replace({ name: ROTA_VISTORIA });
};

/**
 * Recupera a foto/video para conferência
 */
const getFileConferencia = async () => {
  loading.value = true;

  const idVistoriaEtapa = vistoriaStore.currentVistoriaEtapa?.idEtapa;

  if (!idVistoriaEtapa) {
    goVistoria(true);
    return;
  }

  const result = await getFileAndObservation(idVistoriaEtapa);

  if (!result) {
    goVistoria(true);
    return;
  }

  fileContent.value = result.fileContent;
  tipoArquivo = vistoriaStore.currentVistoriaEtapa!.tipo;
  typeMine.value = result.type;
  observacao.value = result?.observacoes || '';
  showObservacao.value = !vistoriaStore.currentVistoriaEtapa!.sync;

  fraseLegivel = `${
    tipoArquivo === TIPO_ETAPA_FOTO ? 'A foto' : 'O vídeo'
  } ficou`;

  if (typeMine.value === TYPE_MINE_PDF) {
    fraseLegivel = 'O arquivo anexado está';
  }

  fraseRetry =
    tipoArquivo === TIPO_ETAPA_FOTO ? 'tirar outra foto' : 'gravar outro vídeo';

  // Tempo para atualizar a tela
  setTimeout(() => {
    loading.value = false;
  }, 300);
};

/**
 * Dados da Store atualizado
 */
vistoriaStore.$subscribe(async () => {
  await getFileConferencia();
});

onMounted(async () => {
  await getFileConferencia();
});

onUnmounted(() => {
  if (fileContent.value) {
    URL.revokeObjectURL(fileContent.value);
  }
});
</script>

<template>
  <!-- Loading -->
  <Loading :show="loading" />

  <ModalWaitSaveFile
    :show="analyzingPhoto"
    message="Estamos analisando a foto"
  />

  <div class="modal modal-sm" :class="{ active: photoApproved }">
    <div class="modal-overlay"></div>
    <div class="modal-container">
      <div class="modal-body verificar__approved">
        <IconCheck />
        <span data-testId="pre-analysis-approved-message">
          Registrado com sucesso.
        </span>
      </div>
    </div>
  </div>

  <div class="verificar">
    <HeaderProgress />

    <div
      class="container verificar__container"
      v-if="!loading && vistoriaStore.currentVistoriaEtapa"
    >
      <!-- Título da Foto -->
      <span class="title">{{
        vistoriaStore.currentVistoriaEtapa.nomeEtapa
      }}</span>
      <span class="subtitle" v-if="!preAnalysisRejectionReason">
        {{ fraseLegivel }} legível?
      </span>

      <div
        class="verificar__rejected"
        v-if="preAnalysisRejectionReason"
        data-testId="pre-analysis-rejection"
      >
        <IconExclamation />
        <div>
          <strong>Não conseguimos ler o documento</strong>
          <p>{{ preAnalysisRejectionReason }}</p>
        </div>
      </div>

      <PreviewFile
        :file-content="fileContent"
        :type-mine="typeMine"
        :tipo-arquivo="tipoArquivo"
      />

      <!-- Observações -->
      <details
        class="accordion"
        :open="observacao !== ''"
        v-if="showObservacao && !preAnalysisRejectionReason"
      >
        <summary class="accordion-header">
          <i class="icon icon-arrow-right"></i>
          <span class="texto"> Adicionar uma observação </span>
        </summary>
        <div class="accordion-body">
          <div class="form-group">
            <textarea
              class="form-input"
              placeholder="Deixe aqui uma observação sobre a foto"
              rows="2"
              v-model="observacao"
            ></textarea>
          </div>
        </div>
      </details>

      <template v-if="preAnalysisRejectionReason">
        <div
          class="btn-vistoria verificar__container__retry"
          v-if="canSendAnyway"
        >
          <button
            class="btn btn-link"
            @click="sendAnyway()"
            data-testId="pre-analysis-send-anyway-button"
          >
            <IconPaperAirplane />
            <span>Enviar mesmo assim</span>
          </button>
        </div>

        <div class="btn-vistoria verificar__container__next">
          <button
            class="btn btn-primary"
            @click="retryPhotoVideo()"
            data-testId="pre-analysis-retake-button"
          >
            <IconCamera />
            <span>Corrigir foto</span>
          </button>
        </div>
      </template>

      <!-- Botão, tirar foto novamente -->
      <div
        class="btn-vistoria verificar__container__retry"
        v-if="!preAnalysisRejectionReason"
      >
        <button
          class="btn btn-link"
          @click="retryPhotoVideo()"
          data-testId="take-photo-again-button"
        >
          <IconCamera v-if="tipoArquivo === TIPO_ETAPA_FOTO" />
          <IconVideoCamera v-if="tipoArquivo === TIPO_ETAPA_VIDEO" />
          <span>Não, {{ fraseRetry }}</span>
        </button>
      </div>

      <!-- Botão, próxima etapa -->
      <div
        class="btn-vistoria verificar__container__next"
        v-if="!preAnalysisRejectionReason"
      >
        <button
          class="btn btn-primary"
          @click="nextStep()"
          data-testId="next-step-button"
        >
          <IconCheck />
          <span>Sim, Continuar</span>
        </button>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.verificar {
  &__rejected {
    display: flex;
    gap: 0.6rem;
    margin: 0.6rem 0;
    padding: 0.8rem;
    border: 1px solid var(--alert);
    border-radius: 0.3rem;
    background-color: #fff7e6;
    color: #7a4100;

    svg {
      flex-shrink: 0;
      width: 1.4rem;
      height: 1.4rem;
    }

    p {
      margin: 0.2rem 0 0;
    }
  }

  &__approved {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    color: var(--success);

    svg {
      width: 1.6rem;
      height: 1.6rem;
    }
  }

  &__container {
    margin-top: 0.8rem;
    margin-bottom: 0.8rem;

    .title,
    .subtitle {
      display: block;
    }

    .accordion {
      margin-top: 0.4rem;

      .accordion-header {
        padding: 0;

        .icon {
          font-size: 0.7rem;
        }
      }

      .accordion-body {
        margin-top: 0.4rem;
      }
    }

    &__retry {
      margin-top: 2rem;
    }

    &__next {
      margin-top: 1rem;

      button {
        padding-top: 0.6rem;
        padding-bottom: 0.6rem;
        height: auto;
      }
    }
  }
}
</style>
